import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { useRedirectIfLoggedIn } from '../hooks/use-auth-hooks';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, AlertTriangle, Check } from "lucide-react";
import { 
  validateSignupForm, 
  hasValidationErrors, 
  FormErrors 
} from '@/utils/form-validation';

export function SignupPage() {
  const { signUp, googleAuth, error } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState<'student' | 'teacher' | undefined>('student');
  const [classGrade, setClassGrade] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  
  // Set page title
  useEffect(() => {
    document.title = 'Authentication - AceHorizon';
  }, []);
  
  // Real-time password confirmation validation
  useEffect(() => {
    if (confirmPassword && password) {
      if (password !== confirmPassword) {
        setValidationErrors(prev => ({
          ...prev,
          confirmPassword: 'Passwords do not match'
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    } else if (confirmPassword) {
      // If confirmPassword has content but password is empty
      setValidationErrors(prev => ({
        ...prev,
        confirmPassword: 'Please enter your password first'
      }));
    }
  }, [password, confirmPassword]);
  
  // Redirect if already logged in
  useRedirectIfLoggedIn();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setValidationErrors({});
    
    // Validate form
    const errors = validateSignupForm(firstName, lastName, email, password, confirmPassword, userType, classGrade);
    if (hasValidationErrors(errors)) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Combine first name and last name into full name
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      
      // Prepare metadata object - include both full name and separate first/last names
      const metadata = { 
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        userType,
        classGrade: userType === 'student' ? classGrade : undefined
      };
      
      console.log('Signup: Submitting with metadata:', metadata);
      
      const result = await signUp(email, password, metadata);
      
      if (result.error) {
        console.error('Signup failed:', result.error);
        
        // Handle specific error cases
        if (result.error.message?.toLowerCase().includes('email')) {
          setValidationErrors({
            email: result.error.message
          });
        } else if (result.error.message?.toLowerCase().includes('password')) {
          setValidationErrors({
            password: result.error.message
          });
        } else {
          setValidationErrors({
            general: result.error.message || 'Signup failed. Please try again.'
          });
        }
      } else {
        // Check if login is required (new in-house API flow)
        if (result.requiresLogin) {
          // User created successfully but needs to login
          console.log('AuthProvider: User signed up successfully, login required');
          
          setSignupSuccess(true);
          
          // Redirect to login page after 3 seconds
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: result.message || 'Account created successfully! Please log in with your credentials.',
                email: email 
              }
            });
          }, 3000);
        } else {
          // Fallback case - show success and redirect to login
          setSignupSuccess(true);
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Account created successfully! Please log in with your credentials.',
                email: email 
              }
            });
          }, 3000);
        }
      }
    } catch (error: any) {
      console.error('Signup failed:', error);
      setValidationErrors({
        general: error.message || 'Signup failed. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleGoogleSignUp = async () => {
    try {
      setIsSubmitting(true);
      // For Google OAuth, we don't pre-store user type since we'll collect it 
      // post-authentication via the user type selection modal
      console.log('Signup: Starting Google OAuth signup without pre-storing user type');
      
      await googleAuth();
      // Note: We don't navigate here as Google OAuth will redirect
    } catch (error) {
      console.error('Google signup failed:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {(error || validationErrors.general) && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error || validationErrors.general}</AlertDescription>
        </Alert>
      )}
      
      {signupSuccess && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">Account Created Successfully!</p>
              <p>You will be redirected to the login page in a few seconds.</p>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {!signupSuccess && (
        <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center pb-6">
          <CardTitle className="text-2xl font-semibold">Create Account</CardTitle>
          <CardDescription>
            Get started with Ace Horizon
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button type="button" variant="outline" className="w-full" onClick={handleGoogleSignUp} disabled={isSubmitting}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
              <path
                d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                fill="currentColor"
              />
            </svg>
            Continue with Google
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Additional profile information will be requested after authentication
          </p>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    // Clear validation error when user starts typing
                    if (validationErrors.firstName) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.firstName;
                        return newErrors;
                      });
                    }
                  }}
                  required
                  className={`h-10 ${validationErrors.firstName ? 'border-red-500 focus:border-red-500' : ''}`}
                />
                {validationErrors.firstName && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {validationErrors.firstName}
                  </p>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    // Clear validation error when user starts typing
                    if (validationErrors.lastName) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.lastName;
                        return newErrors;
                      });
                    }
                  }}
                  required
                  className={`h-10 ${validationErrors.lastName ? 'border-red-500 focus:border-red-500' : ''}`}
                />
                {validationErrors.lastName && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {validationErrors.lastName}
                  </p>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="userType">I am a...</Label>
              <Select
                value={userType}
                onValueChange={(value) => {
                  setUserType(value as 'student' | 'teacher');
                  // Clear class/grade when switching to teacher
                  if (value === 'teacher') {
                    setClassGrade('');
                    // Clear validation error for class/grade
                    if (validationErrors.classGrade) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.classGrade;
                        return newErrors;
                      });
                    }
                  }
                }}
              >
                <SelectTrigger id="userType" className="h-10 w-full">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Conditional Class/Grade field for students */}
            {userType === 'student' && (
              <div className="space-y-2">
                <Label htmlFor="classGrade">Class/Grade</Label>
                <Select
                  value={classGrade}
                  onValueChange={(value) => {
                    setClassGrade(value);
                    // Clear validation error when user selects
                    if (validationErrors.classGrade) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.classGrade;
                        return newErrors;
                      });
                    }
                  }}
                >
                  <SelectTrigger id="classGrade" className={`h-10 w-full ${validationErrors.classGrade ? 'border-red-500 focus:border-red-500' : ''}`}>
                    <SelectValue placeholder="Select your current grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="9">Grade 9</SelectItem>
                    <SelectItem value="10">Grade 10</SelectItem>
                  </SelectContent>
                </Select>
                {validationErrors.classGrade && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {validationErrors.classGrade}
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.email) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.email;
                      return newErrors;
                    });
                  }
                }}
                required
                className={`h-10 ${validationErrors.email ? 'border-red-500 focus:border-red-500' : ''}`}
              />
              {validationErrors.email && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {validationErrors.email}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.password) {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.password;
                      return newErrors;
                    });
                  }
                }}
                required
                className={`h-10 ${validationErrors.password ? 'border-red-500 focus:border-red-500' : ''}`}
              />
              {validationErrors.password && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {validationErrors.password}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    // Note: Real-time validation is handled by useEffect
                  }}
                  required
                  className={`h-10 pr-10 ${
                    validationErrors.confirmPassword 
                      ? 'border-red-500 focus:border-red-500' 
                      : confirmPassword && password && password === confirmPassword
                      ? 'border-green-500 focus:border-green-500'
                      : ''
                  }`}
                />
                {/* Success indicator when passwords match */}
                {confirmPassword && password && password === confirmPassword && !validationErrors.confirmPassword && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Check className="w-4 h-4 text-green-500" />
                  </div>
                )}
                {/* Error indicator when passwords don't match */}
                {validationErrors.confirmPassword && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  </div>
                )}
              </div>
              {/* Error message */}
              {validationErrors.confirmPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {validationErrors.confirmPassword}
                </p>
              )}
              {/* Success message */}
              {confirmPassword && password && password === confirmPassword && !validationErrors.confirmPassword && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting || hasValidationErrors(validationErrors)}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
          
          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="underline underline-offset-4 hover:text-primary">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
      )}
      
      <div className="text-center text-xs text-muted-foreground max-w-md mx-auto">
        By creating an account, you agree to our{" "}
        <Link to="/terms-of-service" className="underline underline-offset-4 hover:text-primary">Terms of Service</Link>{" "}
        and{" "}
        <Link to="/privacy-policy" className="underline underline-offset-4 hover:text-primary">Privacy Policy</Link>.
      </div>
    </div>
  );
}
